import {
	forwardRef,
	Fragment,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import {
	ActionListItem,
	Button,
	DropOverActionList,
	IconButton
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { EmailFilterSelector } from "client/Api/Email/email";
import { isNotNil, setTrackingId } from "client/Misc/utils";

type FilterEntry = {
	value: EmailFilterSelector | null;
	key: LocalizationKey;
};

const filterEntries: readonly FilterEntry[] = [
	{ value: null, key: "_All" },
	{ value: EmailFilterSelector.HasAttachment, key: "_Attachment" },
	{ value: EmailFilterSelector.Flagged, key: "_Flagged" },
	{ value: EmailFilterSelector.Unread, key: "_Unread" }
];

export type FilterOptionSelectorProps = {
	value: EmailFilterSelector | null;
	onChange(value: EmailFilterSelector | null): void;
	isMobile: boolean;
};

export const FilterOptionSelector = (props: FilterOptionSelectorProps): ReactElement => {
	const {
		onChange,
		value,
		isMobile
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const renderCheckbox = useCallback(
		(entry: FilterEntry): ReactElement => {
			const changeHandler = (): void => {
				onChange(entry.value);
				setIsOpen(false);
			};
			const isSelected: boolean = value === entry.value;
			return (
				<ActionListItem
					isActive={isSelected}
					rightIcon={isSelected ? "check" : undefined}
					onClick={changeHandler}
					text={localize(entry.key)}
					trackingId={`filter|${entry.value}`}
				/>
			);
		},
		[onChange, value]
	);

	const clearFilterKey: LocalizationKey | undefined = filterEntries.find(x => x.value === value)
		?.key;

	return (
		<div
			{...setTrackingId("filter-section")}
		>
			{isNotNil(value)
				? (
					<Button
						trackingId="clear-filter"
						kind="transparent"
						icon="xmark"
						text={clearFilterKey ? localize(clearFilterKey) : ""}
						onClick={() => onChange(null)}
						size="large"
					/>
				)
				: (
					<>
						<FilterButton
							onClick={() => setIsOpen(true)}
							isMobile={isMobile}
							ref={ref}
							isActive={isOpen}
						/>
						{isOpen && ref.current && (
							<DropOverActionList
								target={ref.current}
								targetAlignment="br"
								contentAlignment="tr"
								onClose={() => setIsOpen(false)}
								trackingId="email-filter-option__dropover"
								menuTitle={localize("_FilterBy")}
							>
								{filterEntries.map((x => (
									<Fragment key={x.value}>
										{renderCheckbox(x)}
									</Fragment>
								)))}
							</DropOverActionList>
						)}
					</>
				)
			}
		</div>
	);
};
FilterOptionSelector.displayName = "FilterOptionSelector";

type FilterButtonProps = {
	isMobile: boolean;
	isActive: boolean;
	onClick(): void;
};

const FilterButton = forwardRef(
	(props: FilterButtonProps, ref: Ref<HTMLButtonElement>): ReactElement => {
		const {
			onClick,
			isMobile,
			isActive
		} = props;

		return (
			<IconButton
				trackingId="filter-button"
				onClick={onClick}
				kind={isMobile
					? "secondary"
					: "transparent"}
				ref={ref}
				icon="filter"
				shape={isMobile
					? "square"
					: "round"
				}
				isActive={isActive}
				size={isMobile ? "large" : "normal"}
			/>
		);
	}
);
FilterButton.displayName = "FilterButton";
